import React, { useEffect, useRef, useState } from 'react';
import { formatMoney } from '../../../utils/currency';
import type { CurrencyCode } from '@bbpos/types';
import { Modal } from '../../CompositeComponents/Modal';
import type { ReceiptImageViewerProps } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(paise: number, currency: CurrencyCode) {
  return formatMoney(paise, currency);
}

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.5, 2, 3];

// ─── ReceiptImageViewer ───────────────────────────────────────────────────────

export function ReceiptImageViewer({
  imageUrl,
  expenseDetails,
  onClose,
  onApprove,
  onReject,
  loading = false,
  currency = 'INR',
  className,
}: ReceiptImageViewerProps) {
  const [zoomIdx, setZoomIdx] = useState(2); // default 1×
  const [rotation, setRotation] = useState(0);
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const zoom = ZOOM_LEVELS[zoomIdx];
  const altText = `Expense receipt from ${expenseDetails.submittedBy} for ${fmt(expenseDetails.amount, currency)}`;

  // Keyboard shortcuts: +/- zoom, R rotate, Escape close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === '+' || e.key === '=') setZoomIdx((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1));
      if (e.key === '-') setZoomIdx((i) => Math.max(0, i - 1));
      if (e.key === 'r' || e.key === 'R') setRotation((r) => (r + 90) % 360);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Reset image state when URL changes
  useEffect(() => {
    setImgError(false);
    setImgLoaded(false);
  }, [imageUrl]);

  const dateStr = expenseDetails.date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Receipt Viewer"
      variant="wide"
      className={className}
      footer={
        (onApprove || onReject) ? (
          <div className="flex gap-2">
            {onApprove && (
              <button
                type="button"
                onClick={onApprove}
                className="flex-1 py-2 bg-success-600 text-white text-sm font-medium rounded-lg hover:bg-success-700 transition-colors"
              >
                Approve
              </button>
            )}
            {onReject && !showRejectInput && (
              <button
                type="button"
                onClick={() => setShowRejectInput(true)}
                className="flex-1 py-2 bg-danger-50 text-danger-700 border border-danger-300 text-sm font-medium rounded-lg hover:bg-danger-100 transition-colors"
              >
                Reject
              </button>
            )}
            {onReject && showRejectInput && (
              <div className="flex flex-1 gap-2">
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Rejection reason..."
                  className="flex-1 border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  autoFocus
                />
                <button
                  type="button"
                  disabled={!rejectReason.trim()}
                  onClick={() => { onReject(rejectReason.trim()); setShowRejectInput(false); }}
                  className="py-1.5 px-3 bg-danger-600 text-white text-sm rounded-md hover:bg-danger-700 disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setShowRejectInput(false)}
                  className="py-1.5 px-2 border border-neutral-300 text-neutral-600 text-sm rounded-md hover:bg-neutral-50"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        ) : undefined
      }
    >
      <div className="flex gap-4">
        {/* Image panel */}
        <div className="flex-1 min-w-0">
          {/* Zoom / rotate controls */}
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
              disabled={zoomIdx === 0}
              aria-label="Zoom out"
              className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-100 disabled:opacity-40"
            >
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M5 7h4M11 11l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <span className="text-xs text-neutral-600 tabular-nums min-w-[32px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setZoomIdx((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
              disabled={zoomIdx === ZOOM_LEVELS.length - 1}
              aria-label="Zoom in"
              className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-100 disabled:opacity-40"
            >
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M7 5v4M5 7h4M11 11l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setRotation((r) => (r + 90) % 360)}
              aria-label="Rotate image 90 degrees"
              className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-100 ml-1"
            >
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M13 4H8a5 5 0 000 10h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M11 2l2 2-2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="text-xs text-neutral-400 ml-auto">
              Use +/- to zoom, R to rotate, Esc to close
            </span>
          </div>

          {/* Image area */}
          <div className="overflow-auto bg-neutral-100 rounded-lg flex items-center justify-center min-h-[300px] max-h-[60vh]">
            {loading || (!imgLoaded && !imgError) ? (
              <span
                className="block w-48 h-64 rounded bg-neutral-200 animate-pulse-soft"
                aria-busy="true"
                aria-label="Loading receipt image"
              />
            ) : imgError ? (
              <div className="flex flex-col items-center gap-2 text-neutral-400 py-8">
                <svg className="w-12 h-12" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                  <rect x="6" y="6" width="36" height="36" rx="4" stroke="currentColor" strokeWidth="2" />
                  <path d="M18 24l4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p className="text-sm">Image not available</p>
              </div>
            ) : null}
            <img
              src={imageUrl}
              alt={altText}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              className={[
                'transition-transform duration-200 max-w-none',
                imgLoaded && !imgError ? 'block' : 'hidden',
              ].join(' ')}
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: 'center center',
              }}
            />
          </div>
        </div>

        {/* Expense details panel */}
        <div className="w-48 shrink-0 space-y-3 text-sm">
          <h3 className="font-semibold text-neutral-800 text-sm">Expense Details</h3>
          <dl className="space-y-2">
            <div>
              <dt className="text-xs text-neutral-500">Amount</dt>
              <dd className="font-bold text-neutral-900 tabular-nums">{fmt(expenseDetails.amount, currency)}</dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Category</dt>
              <dd className="font-medium text-neutral-800">{expenseDetails.category}</dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Date</dt>
              <dd className="text-neutral-700">{dateStr}</dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Submitted by</dt>
              <dd className="text-neutral-700">{expenseDetails.submittedBy}</dd>
            </div>
            {expenseDetails.notes && (
              <div>
                <dt className="text-xs text-neutral-500">Notes</dt>
                <dd className="text-neutral-600 text-xs">{expenseDetails.notes}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </Modal>
  );
}
